/**
 * Area `help` of the console gateway — over `apps/api/src/routes/help/` and `apps/api/src/routes/public-help/` (design
 * §31.4.1–§31.4.2). Owned by plan-019 unit **WAPP-HOST**: the portal host's reads and writes — whether help is on, the
 * grant (signed in, or anonymous on a signed-out page with its own solved challenge), the unread count — and, for the
 * help panel and the `/help` fallback page, the person's own help requests and sending one.
 *
 * ```
 *   readHelpStatus             `getPublicHelpStatus`       GET  /v1/public/help/status        anyone; cached a minute by the api
 *   createHelpGrant            `createHelpGrant`            POST /v1/help/grants               the signed-in person (never an operator acting as them)
 *   readPublicHelpChallenge    `createPublicHelpChallenge`  GET  /v1/public/help/challenge     anyone; the body, for `challengeOf`
 *   createPublicHelpGrant      `createPublicHelpGrant`      POST /v1/public/help/grants        a visitor, with a solved challenge
 *   listHelpRequests           `listHelpRequests`           GET  /v1/help/requests             the person's own; `unreadCount` across pages
 *   readHelpRequest            `getHelpRequest`             GET  /v1/help/requests/{id}        the person's own, with replies
 *   sendHelpRequest            `createHelpRequest`          POST /v1/help/requests             Idempotency-Key required
 *   sendPublicHelpRequest      `createPublicHelpRequest`    POST /v1/public/help/requests      a visitor; its own solved challenge
 *   markHelpRequestRead        `markHelpRequestRead`        POST /v1/help/requests/{id}/read
 *   carryHelpApiRequest        the six request operations above, by name — for the widget's `HelpHostApi.helpApi`
 *   carryHelpAccountAction     plan-025: a confirmed confirm card's registry request (`../../help/account-action-client.ts`)
 *   readHelpPageReading        `getHelpPageReading`         GET  /v1/help/page-reading         the person's own choice (ADR 0072)
 *   setHelpPageReading         `setHelpPageReading`         PUT  /v1/help/page-reading         allowed | declined, audited
 * ```
 *
 * `carryHelpApiRequest` is the widget's own door to the request routes (WID-FEAT's request form and "Your messages"
 * build the wire bodies themselves, so the preview is exactly what is sent): the portal's api client carries them —
 * same origin, the auth_session cookie, the CSRF token and the scope generation — and answers the status and the body
 * whatever the status, with a `429`'s `Retry-After`; it rejects only when no answer arrived.
 *
 * The register is EMPTY: every operation is in the assembled spec. The sandbox half is `../sandbox/help.ts`. The fill
 * registry (`apps/web-app/src/help/fill/**`) never imports this module: a fill sends nothing (the `help-fill-no-network`
 * rule).
 */
import { assertOk, type ApiResult, type WeTalkApiClient } from "@wetalk/api-client";
import {
  isHelpPageReading,
  type HelpGrantAnswer,
  type HelpLanguage,
  type HelpPageReading,
  type HelpPageReadingRevision,
} from "@wetalk/domain";
import type { HelpAccountActionAnswer, HelpAccountActionRequest, HelpApiAnswer, HelpApiRequest } from "@wetalk/help-widget";

import type { ConsoleGateway } from "../gateway.js";
import { MalformedResponseError, readArray, readBoolean, readRecord, readText, readWholeNumber } from "../decode.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

export const HELP_REQUEST_KIND_VIEW = ["feedback", "support"] as const;
export type HelpRequestKindView = (typeof HELP_REQUEST_KIND_VIEW)[number];

export const HELP_REQUEST_STATE_VIEW = ["new", "in_progress", "answered", "closed"] as const;
export type HelpRequestStateView = (typeof HELP_REQUEST_STATE_VIEW)[number];

/** How a request was started, as the api lists it. A page may send only the first three. */
export const HELP_REQUEST_ORIGIN_VIEW = ["widget_form", "assistant_offered", "person_unavailable", "operator_created"] as const;
export type HelpRequestOriginView = (typeof HELP_REQUEST_ORIGIN_VIEW)[number];
export type HelpRequestSentOrigin = Exclude<HelpRequestOriginView, "operator_created">;

/** What the signed-in grant names besides the person (design §31.4.1): where the panel was opened. */
export interface HelpGrantAsk {
  readonly language: HelpLanguage;
  readonly routeId: string | null;
  readonly screenLabel: string | null;
}

/** An anonymous visitor's grant on a signed-out portal page (design §31.4.2). */
export interface PublicHelpGrantAsk extends HelpGrantAsk {
  /** The solved challenge (`altchaPayloadOf`). */
  readonly altcha: string;
  readonly visitorKey: string;
}

/** One of the person's help requests ("Your messages"). */
export interface HelpRequestView {
  readonly helpRequestId: string;
  readonly reference: string;
  readonly kind: HelpRequestKindView;
  readonly state: HelpRequestStateView;
  readonly origin: HelpRequestOriginView;
  /** The first 80 characters; `null` once erased. */
  readonly excerpt: string | null;
  readonly unread: boolean;
  readonly createdAt: string;
  readonly stateChangedAt: string;
  readonly lastReplyAt: string | null;
  readonly closedAt: string | null;
}

export interface HelpRequestPageView {
  readonly request: readonly HelpRequestView[];
  readonly nextCursor: string | null;
  /** Requests with a reply the person has not opened yet, across every page. */
  readonly unreadCount: number;
}

export interface HelpRequestReplyView {
  readonly helpRequestReplyId: string;
  /** `null` once erased. */
  readonly text: string | null;
  readonly createdAt: string;
  readonly readAt: string | null;
}

export interface HelpRequestDetailView extends HelpRequestView {
  readonly text: string | null;
  readonly language: HelpLanguage;
  readonly conversationId: string | null;
  readonly erased: boolean;
  readonly reply: readonly HelpRequestReplyView[];
}

/** A request as a signed-in person sends it (`HelpRequestCreate`). */
export interface HelpRequestDraft {
  readonly kind: HelpRequestKindView;
  readonly text: string;
  readonly origin: HelpRequestSentOrigin;
  readonly routeId: string;
  readonly screenLabel: string | null;
  readonly language: HelpLanguage;
  /** The build of the page the request was written on. */
  readonly consoleBuild: string;
  /** The privacy notice the person saw (`HELP_NOTICE_REVISION`). */
  readonly noticeRevision: string;
  readonly conversationId: string | null;
}

/** A request as a visitor on a signed-out portal page sends it (`PublicHelpRequestCreate`, surface `portal`). */
export interface PublicHelpRequestDraft extends HelpRequestDraft {
  readonly altcha: string;
  readonly visitorKey: string;
  /** Where a reply may be emailed; kept only with {@link consentRevision}. */
  readonly contactEmail: string | null;
  readonly consentRevision: string | null;
}

export interface HelpRequestSentView {
  readonly helpRequestId: string;
  readonly reference: string;
  /** True when this key had already stored the request; nothing was sent again. */
  readonly replay: boolean;
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /** plan-019 WAPP-HOST: whether the help assistant is on and published (shows the launcher and the Help link). */
    readHelpStatus(): Promise<{ readonly enabled: boolean }>;
    /** plan-019 WAPP-HOST: a single-use grant for the signed-in person (`403 help_unavailable_impersonating`, `503 help_unavailable`). */
    createHelpGrant(ask: HelpGrantAsk): Promise<HelpGrantAnswer>;
    /** plan-019 WAPP-HOST: a fresh challenge for an anonymous grant or request — the response body, for `challengeOf`. */
    readPublicHelpChallenge(): Promise<unknown>;
    /** plan-019 WAPP-HOST: a single-use grant for a visitor on a signed-out page (no open conversation is named). */
    createPublicHelpGrant(ask: PublicHelpGrantAsk): Promise<HelpGrantAnswer>;
    /** plan-019 WAPP-HOST: one page of the person's own help requests, newest first, and the unread count. */
    listHelpRequests(cursor: string | null): Promise<HelpRequestPageView>;
    /** plan-019 WAPP-HOST: one of the person's own help requests with its replies (`404 help_request_not_found` otherwise). */
    readHelpRequest(helpRequestId: string): Promise<HelpRequestDetailView>;
    /** plan-019 WAPP-HOST: send a request; a retry with the same key answers the first (`replay: true`). */
    sendHelpRequest(draft: HelpRequestDraft, idempotencyKey: string): Promise<HelpRequestSentView>;
    /** plan-019 WAPP-HOST: a visitor's request on a signed-out page, with its own solved challenge. */
    sendPublicHelpRequest(draft: PublicHelpRequestDraft, idempotencyKey: string): Promise<HelpRequestSentView>;
    /** plan-019 WAPP-HOST: clear the unread mark of one of the person's requests. */
    markHelpRequestRead(helpRequestId: string): Promise<void>;
    /**
     * plan-019 WAPP-HOST: one of the widget's help request requests (`HelpHostApi.helpApi`), carried by the portal's api
     * client; resolves with the answer whatever its status, rejects only when none arrived.
     */
    carryHelpApiRequest(request: HelpApiRequest): Promise<HelpApiAnswer>;
    /**
     * plan-025 A-WAPP (design §36.10): a confirmed help confirm card's request, sent as the person's own with the card's
     * id as its idempotency key; answers the status and error code whatever they are, rejects only when none arrived.
     */
    carryHelpAccountAction(request: HelpAccountActionRequest, helpActionId: string): Promise<HelpAccountActionAnswer>;
    /** ADR 0072 (2026-10-18): whether the help assistant may read the whole page — the person's own choice. */
    readHelpPageReading(): Promise<HelpPageReadingView>;
    /** ADR 0072: records the person's answer (audited in every organization they belong to) and answers the choice now. */
    /**
     * ADR 0076 (review finding 1): `consentRevision` is the wording the calling surface SHOWED with its Allow — never
     * one it did not draw.
     */
    setHelpPageReading(choice: "allowed" | "declined", consentRevision: HelpPageReadingRevision): Promise<HelpPageReadingView>;
    /** OD-3 (owner, 2026-10-21): asked again, the person keeps the assistant-only answer (`kept_for_assistant`). */
    keepHelpPageReadingForAssistant(): Promise<HelpPageReadingView>;
  }
}

/** The person's page-reading choice (`HelpPageReading` on the wire). */
export interface HelpPageReadingView {
  readonly choice: HelpPageReading;
  /** ISO instant of the last answer; `null` while unasked. */
  readonly changedAt: string | null;
  /**
   * ADR 0076: whether the answer also lets a wetalk person see the page (`operator_sees`: `allowed` under the newer
   * wording). An answer that does not say so reads `false` — the page then reaches the assistant only.
   */
  readonly operatorSees: boolean;
  /**
   * OD-3 (owner, 2026-10-21): `allowed` for the assistant only and not yet asked again under the newer wording
   * (`ask_again`) — the panel and My security ask once more. An answer that does not say so reads `false`.
   */
  readonly askAgain: boolean;
}

export function helpPageReadingViewOf(wire: unknown, path: string): HelpPageReadingView {
  const data = readRecord(wire, path);
  const choice = readText(data["choice"], `${path}.choice`);
  if (!isHelpPageReading(choice)) throw new MalformedResponseError(`${path}.choice`, "allowed | declined | unasked", choice);
  const changed = data["changed_at"];
  const operatorSees = data["operator_sees"];
  if (operatorSees !== undefined && typeof operatorSees !== "boolean") throw new MalformedResponseError(`${path}.operator_sees`, "a boolean", operatorSees);
  const askAgain = data["ask_again"];
  if (askAgain !== undefined && typeof askAgain !== "boolean") throw new MalformedResponseError(`${path}.ask_again`, "a boolean", askAgain);
  return {
    choice,
    changedAt: changed === null || changed === undefined ? null : readText(changed, `${path}.changed_at`),
    operatorSees: operatorSees === true,
    askAgain: askAgain === true,
  };
}

/** The methods this slice puts on `ConsoleGateway`. */
export type HelpGatewaySlice = Pick<
  ConsoleGateway,
  | "readHelpStatus"
  | "createHelpGrant"
  | "readPublicHelpChallenge"
  | "createPublicHelpGrant"
  | "listHelpRequests"
  | "readHelpRequest"
  | "sendHelpRequest"
  | "sendPublicHelpRequest"
  | "markHelpRequestRead"
  | "carryHelpApiRequest"
  | "carryHelpAccountAction"
  | "readHelpPageReading"
  | "setHelpPageReading"
  | "keepHelpPageReadingForAssistant"
>;

/** This area's rows of `GATEWAY_GAP`: none — every operation is served. */
export const helpGap: readonly GatewayGap[] = [];

// ── the wire ─────────────────────────────────────────────────────────────────────────────────────────────

function oneOf<T extends string>(allowed: readonly T[], value: unknown, path: string): T {
  const raw = readText(value, path);
  const found = allowed.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, allowed.join(" | "), raw);
  return found;
}

function nullableText(value: unknown, path: string): string | null {
  return value === null ? null : readText(value, path);
}

function dataOf(body: unknown, operationId: string): unknown {
  return readRecord(body, operationId)["data"];
}

/** `HelpGrant` / `PublicHelpGrant` → the answer the widget takes; the public one names no open conversation. */
export function helpGrantAnswerOf(wire: unknown, path: string): HelpGrantAnswer {
  const data = readRecord(wire, path);
  const open = data["open_conversation_id"];
  return {
    grant: readText(data["grant"], `${path}.grant`),
    socket_url: readText(data["socket_url"], `${path}.socket_url`),
    expires_at: readText(data["expires_at"], `${path}.expires_at`),
    open_conversation_id: open === undefined || open === null ? null : readText(open, `${path}.open_conversation_id`),
  };
}

export function helpRequestViewOf(wire: unknown, path: string): HelpRequestView {
  const row = readRecord(wire, path);
  return {
    helpRequestId: readText(row["help_request_id"], `${path}.help_request_id`),
    reference: readText(row["reference"], `${path}.reference`),
    kind: oneOf(HELP_REQUEST_KIND_VIEW, row["kind"], `${path}.kind`),
    state: oneOf(HELP_REQUEST_STATE_VIEW, row["state"], `${path}.state`),
    origin: oneOf(HELP_REQUEST_ORIGIN_VIEW, row["origin"], `${path}.origin`),
    excerpt: nullableText(row["excerpt"], `${path}.excerpt`),
    unread: readBoolean(row["unread"], `${path}.unread`),
    createdAt: readText(row["created_at"], `${path}.created_at`),
    stateChangedAt: readText(row["state_changed_at"], `${path}.state_changed_at`),
    lastReplyAt: nullableText(row["last_reply_at"], `${path}.last_reply_at`),
    closedAt: nullableText(row["closed_at"], `${path}.closed_at`),
  };
}

export function helpRequestPageOf(body: unknown, path: string): HelpRequestPageView {
  const page = readRecord(body, path);
  return {
    request: readArray(page["data"], `${path}.data`).map((one, index) => helpRequestViewOf(one, `${path}.data[${String(index)}]`)),
    nextCursor: nullableText(page["next_cursor"], `${path}.next_cursor`),
    unreadCount: readWholeNumber(page["unread_count"], `${path}.unread_count`),
  };
}

export function helpRequestDetailOf(wire: unknown, path: string): HelpRequestDetailView {
  const row = readRecord(wire, path);
  return {
    ...helpRequestViewOf(wire, path),
    text: nullableText(row["text"], `${path}.text`),
    language: oneOf(["en", "el"] as const, row["language"], `${path}.language`),
    conversationId: nullableText(row["conversation_id"], `${path}.conversation_id`),
    erased: readBoolean(row["erased"], `${path}.erased`),
    reply: readArray(row["reply"], `${path}.reply`).map((one, index) => {
      const at = `${path}.reply[${String(index)}]`;
      const reply = readRecord(one, at);
      return {
        helpRequestReplyId: readText(reply["help_request_reply_id"], `${at}.help_request_reply_id`),
        text: nullableText(reply["text"], `${at}.text`),
        createdAt: readText(reply["created_at"], `${at}.created_at`),
        readAt: nullableText(reply["read_at"], `${at}.read_at`),
      };
    }),
  };
}

function helpRequestSentOf(wire: unknown, path: string): HelpRequestSentView {
  const data = readRecord(wire, path);
  return {
    helpRequestId: readText(data["help_request_id"], `${path}.help_request_id`),
    reference: readText(data["reference"], `${path}.reference`),
    replay: readBoolean(data["replay"], `${path}.replay`),
  };
}

/** `HelpRequestCreate` on the wire. */
function helpRequestBodyOf(draft: HelpRequestDraft): Readonly<Record<string, unknown>> {
  return {
    kind: draft.kind,
    text: draft.text,
    origin: draft.origin,
    route_id: draft.routeId,
    screen_label: draft.screenLabel,
    language: draft.language,
    console_build: draft.consoleBuild,
    notice_revision: draft.noticeRevision,
    conversation_id: draft.conversationId,
  };
}

/** The generated client's call for one widget request. */
function resultOf(client: WeTalkApiClient, request: HelpApiRequest): Promise<ApiResult> {
  switch (request.operation) {
    case "createHelpRequest":
      return client.createHelpRequest({ body: request.body, header: { "idempotency-key": request.idempotencyKey } });
    case "createPublicHelpRequest":
      return client.createPublicHelpRequest({ body: request.body, header: { "idempotency-key": request.idempotencyKey } });
    case "createPublicHelpChallenge":
      return client.createPublicHelpChallenge();
    case "listHelpRequests":
      return client.listHelpRequests({ query: request.cursor === null ? {} : { cursor: request.cursor } });
    case "getHelpRequest":
      return client.getHelpRequest({ path: { help_request_id: request.helpRequestId } });
    case "markHelpRequestRead":
      return client.markHelpRequestRead({ path: { help_request_id: request.helpRequestId } });
  }
}

export function helpApiSlice(client: WeTalkApiClient, context: SliceContext): HelpGatewaySlice {
  return {
    async readHelpStatus() {
      const answer = await client.getPublicHelpStatus();
      const data = readRecord(dataOf(assertOk(answer), "getPublicHelpStatus"), "getPublicHelpStatus.data");
      return { enabled: readBoolean(data["enabled"], "getPublicHelpStatus.data.enabled") };
    },
    async createHelpGrant(ask) {
      const answer = await client.createHelpGrant({
        body: { language: ask.language, route_id: ask.routeId, screen_label: ask.screenLabel },
      });
      return helpGrantAnswerOf(dataOf(assertOk(answer), "createHelpGrant"), "createHelpGrant.data");
    },
    async readPublicHelpChallenge() {
      return assertOk(await client.createPublicHelpChallenge());
    },
    async createPublicHelpGrant(ask) {
      const answer = await client.createPublicHelpGrant({
        body: {
          altcha: ask.altcha,
          visitor_key: ask.visitorKey,
          surface: "portal",
          language: ask.language,
          page_path: null,
          route_id: ask.routeId,
          screen_label: ask.screenLabel,
        },
      });
      return helpGrantAnswerOf(dataOf(assertOk(answer), "createPublicHelpGrant"), "createPublicHelpGrant.data");
    },
    async listHelpRequests(cursor) {
      const answer = await client.listHelpRequests({ query: cursor === null ? {} : { cursor } });
      return helpRequestPageOf(assertOk(answer), "listHelpRequests");
    },
    async readHelpRequest(helpRequestId) {
      const answer = await client.getHelpRequest({ path: { help_request_id: helpRequestId } });
      return helpRequestDetailOf(dataOf(assertOk(answer), "getHelpRequest"), "getHelpRequest.data");
    },
    async sendHelpRequest(draft, idempotencyKey) {
      const answer = await client.createHelpRequest({
        body: helpRequestBodyOf(draft),
        header: { "idempotency-key": idempotencyKey },
      });
      return helpRequestSentOf(dataOf(assertOk(answer), "createHelpRequest"), "createHelpRequest.data");
    },
    async sendPublicHelpRequest(draft, idempotencyKey) {
      const answer = await client.createPublicHelpRequest({
        body: {
          ...helpRequestBodyOf(draft),
          altcha: draft.altcha,
          visitor_key: draft.visitorKey,
          surface: "portal",
          page_path: null,
          contact_email: draft.contactEmail,
          consent_revision: draft.consentRevision,
        },
        header: { "idempotency-key": idempotencyKey },
      });
      return helpRequestSentOf(dataOf(assertOk(answer), "createPublicHelpRequest"), "createPublicHelpRequest.data");
    },
    async markHelpRequestRead(helpRequestId) {
      assertOk(await client.markHelpRequestRead({ path: { help_request_id: helpRequestId } }));
    },
    async readHelpPageReading() {
      const answer = await client.getHelpPageReading();
      return helpPageReadingViewOf(dataOf(assertOk(answer), "getHelpPageReading"), "getHelpPageReading.data");
    },
    async setHelpPageReading(choice, consentRevision) {
      const answer = await client.setHelpPageReading({ body: { choice, consent_revision: consentRevision } });
      return helpPageReadingViewOf(dataOf(assertOk(answer), "setHelpPageReading"), "setHelpPageReading.data");
    },
    async keepHelpPageReadingForAssistant() {
      const answer = await client.setHelpPageReading({ body: { kept_for_assistant: true } });
      return helpPageReadingViewOf(dataOf(assertOk(answer), "setHelpPageReading"), "setHelpPageReading.data");
    },
    async carryHelpApiRequest(request) {
      const result = await resultOf(client, request);
      return {
        status: result.status,
        body: result.body === undefined ? null : result.body,
        retryAfterSecond: result.status === 429 ? context.takeRetryAfterSecond(result.requestId) : null,
      };
    },
    async carryHelpAccountAction(request, helpActionId) {
      const result = await context.sendHelpAccountAction(request, helpActionId);
      const code = (result.body as { readonly error?: { readonly code?: unknown } } | null)?.error?.code;
      return { status: result.status, code: typeof code === "string" && /^[a-z][a-z0-9_]{0,63}$/u.test(code) ? code : null };
    },
  };
}
